<template>
  <Teleport to="body">
    <Transition name="ms-fade">
      <div v-if="open" class="ms-root">
        <div class="ms-scrim" @click="close" />
        <div class="ms-panel" role="dialog" aria-modal="true" aria-label="All screens">
          <div class="ms-grip" />
          <div class="ms-search">
            <VaIcon name="search" size="18px" color="secondary" />
            <input
              ref="searchEl"
              v-model="query"
              type="text"
              class="ms-input"
              placeholder="Search screens…"
              aria-label="Search screens"
            />
            <VaIcon v-if="query" name="close" size="16px" color="secondary" role="button" aria-label="Clear" @click="query = ''" />
          </div>

          <div class="ms-scroll">
            <template v-if="query.trim()">
              <button v-for="leaf in searchResults" :key="leaf.name" class="ms-item" @click="go(leaf)">
                <VaIcon :name="leaf.meta.icon" size="20px" />
                <span class="ms-label">{{ t(leaf.displayName) }}</span>
                <VaIcon v-if="isLocked(leaf)" name="mso-lock" size="14px" color="secondary" />
              </button>
              <p v-if="!searchResults.length" class="ms-empty">No screens match “{{ query.trim() }}”.</p>
            </template>

            <template v-else>
              <template v-for="(entry, i) in allowedRoutes" :key="i">
                <button v-if="!entry.children?.length && entry.name" class="ms-item ms-item--solo" @click="go(entry)">
                  <VaIcon :name="entry.meta.icon" size="20px" />
                  <span class="ms-label">{{ t(entry.displayName) }}</span>
                  <VaIcon v-if="isLocked(entry)" name="mso-lock" size="14px" color="secondary" />
                </button>
                <div v-else-if="entry.children?.length" class="ms-group">
                  <div class="ms-group-label">{{ t(entry.displayName) }}</div>
                  <button v-for="child in entry.children" :key="child.name" class="ms-item" @click="go(child)">
                    <VaIcon :name="child.meta.icon" size="20px" />
                    <span class="ms-label">{{ t(child.displayName) }}</span>
                    <VaIcon v-if="isLocked(child)" name="mso-lock" size="14px" color="secondary" />
                  </button>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useNavTree } from '../../composables/useNavTree'
import type { INavigationRoute } from '../sidebar/NavigationRoutes'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [boolean] }>()

const { t } = useI18n()
const router = useRouter()
const { allowedRoutes, isLocked } = useNavTree()

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const query = ref('')
const searchEl = ref<HTMLInputElement | null>(null)

// Flatten every navigable leaf (an entry with a route name) for the search view.
const leaves = computed(() => {
  const out: INavigationRoute[] = []
  const walk = (routes: INavigationRoute[]) => {
    for (const r of routes) {
      if (r.name) out.push(r)
      if (r.children?.length) walk(r.children)
    }
  }
  walk(allowedRoutes.value)
  return out
})

const searchResults = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return []
  return leaves.value.filter((r) => t(r.displayName).toLowerCase().includes(q))
})

const close = () => (open.value = false)

const go = (route: INavigationRoute) => {
  if (route.name) router.push({ name: route.name })
  close()
}

// Clear on close; focus the field on open.
watch(open, (v) => {
  if (!v) {
    query.value = ''
  } else {
    nextTick(() => searchEl.value?.focus())
  }
})
</script>

<style scoped lang="scss">
.ms-root {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.ms-scrim {
  position: absolute;
  inset: 0;
  background: rgba(8, 14, 20, 0.5);
}
.ms-panel {
  position: relative;
  background: var(--va-background-secondary, #fff);
  border-radius: 18px 18px 0 0;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -14px 40px -18px rgba(0, 0, 0, 0.45);
  padding-bottom: env(safe-area-inset-bottom);
}
.ms-grip {
  width: 40px;
  height: 4px;
  border-radius: 999px;
  background: var(--va-background-border, #d5dbe2);
  margin: 10px auto 4px;
}
.ms-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 14px 10px;
  padding: 9px 12px;
  border-radius: 11px;
  background: var(--va-background-element, #f2f5f8);
  border: 1px solid var(--va-background-border, #e2e7ec);
}
.ms-input {
  flex: 1;
  border: 0;
  background: transparent;
  outline: none;
  font-size: 15px;
  color: var(--va-text-primary, #1a2530);
}
.ms-scroll {
  overflow-y: auto;
  padding: 2px 10px 14px;
  -webkit-overflow-scrolling: touch;
}
.ms-group {
  margin-top: 6px;
}
.ms-group-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--va-secondary, #6b7a89);
  padding: 12px 12px 5px;
}
.ms-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 12px;
  border: 0;
  background: transparent;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 600;
  color: var(--va-text-primary, #1a2530);
  cursor: pointer;
  text-align: left;
}
.ms-item:active {
  background: var(--va-background-element, #eef2f6);
}
.ms-item--solo {
  margin-top: 2px;
}
.ms-label {
  flex: 1;
}
.ms-empty {
  color: var(--va-secondary, #6b7a89);
  font-size: 14px;
  padding: 18px 12px;
  margin: 0;
}

@media (hover: hover) {
  .ms-item:hover {
    background: var(--va-background-element, #eef2f6);
  }
}

.ms-fade-enter-active,
.ms-fade-leave-active {
  transition: opacity 0.2s ease;
}
.ms-fade-enter-active .ms-panel,
.ms-fade-leave-active .ms-panel {
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
}
.ms-fade-enter-from,
.ms-fade-leave-to {
  opacity: 0;
}
.ms-fade-enter-from .ms-panel,
.ms-fade-leave-to .ms-panel {
  transform: translateY(100%);
}
@media (prefers-reduced-motion: reduce) {
  .ms-fade-enter-active .ms-panel,
  .ms-fade-leave-active .ms-panel {
    transition: none;
  }
}
</style>
